import type { MouseEvent } from 'react';
import { createSeoSlug } from './seo';

export type AppPage =
  | 'home'
  | 'login'
  | 'register'
  | 'resetPassword'
  | 'verifyEmail'
  | 'search'
  | 'category'
  | 'productDetail'
  | 'userDetails'
  | 'profile'
  | 'favorites'
  | 'settings'
  | 'locations'
  | 'myAnnouncements'
  | 'createProduct'
  | 'editProduct'
  | 'messages'
  | 'orders'
  | 'orderStatistics'
  | 'orderReport'
  | 'administration'
  | 'terms'
  | 'privacy'
  | 'cookies'
  | 'about'
  | 'contact'
  | 'searchRankingHelp'
  | 'notFound';

export interface AppRoute {
  page: AppPage;
  params: URLSearchParams;
  productId?: string;
  userId?: string;
  token?: string;
}

const staticPaths: Partial<Record<AppPage, string>> = {
  home: '/',
  login: '/login',
  register: '/register',
  resetPassword: '/reset-password',
  verifyEmail: '/verify-email',
  search: '/search',
  profile: '/profile',
  favorites: '/favorites',
  settings: '/settings',
  locations: '/locations',
  myAnnouncements: '/my-announcements',
  createProduct: '/announcements/new',
  messages: '/messages',
  orders: '/orders',
  orderStatistics: '/order-statistics',
  orderReport: '/order-report',
  administration: '/administration',
  terms: '/terms',
  privacy: '/privacy',
  cookies: '/cookies',
  about: '/about',
  contact: '/contact',
  searchRankingHelp: '/help/search-ranking',
};

const pathPages = new Map(
  Object.entries(staticPaths).map(([page, path]) => [path, page as AppPage])
);

const normalizePath = (pathname: string): string => {
  if (pathname === '/') {
    return pathname;
  }
  return pathname.replace(/\/+$/, '');
};

const matchPathParameter = (pathname: string, pattern: RegExp): string | null => {
  const match = pathname.match(pattern);
  return match ? decodeURIComponent(match[1]) : null;
};

const appendQuery = (path: string, params: URLSearchParams): string => {
  const query = params.toString();
  return query ? `${path}?${query}` : path;
};

export const createPageUrl = (
  page: AppPage,
  values: Record<string, string | null | undefined> = {}
): string => {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value) {
      params.set(key, value);
    }
  });

  if (page === 'productDetail') {
    const productId = params.get('id');
    if (!productId) return '/';
    const slug = params.get('slug') || createSeoSlug(params.get('title'), 'anunt');
    params.delete('id');
    params.delete('slug');
    params.delete('title');
    params.delete('shareRedirect');
    return appendQuery(`/products/${encodeURIComponent(slug)}/${encodeURIComponent(productId)}`, params);
  }

  if (page === 'userDetails') {
    const userId = params.get('id');
    if (!userId) return '/';
    const slug = params.get('slug') || createSeoSlug(params.get('name'), 'producator');
    params.delete('id');
    params.delete('slug');
    params.delete('name');
    return appendQuery(`/users/${encodeURIComponent(slug)}/${encodeURIComponent(userId)}`, params);
  }

  if (page === 'category') {
    const categoryId = params.get('id');
    if (!categoryId) return '/';
    const slug = params.get('slug') || createSeoSlug(params.get('name'), 'categorie');
    params.delete('id');
    params.delete('slug');
    params.delete('name');
    return appendQuery(`/categories/${encodeURIComponent(slug)}/${encodeURIComponent(categoryId)}`, params);
  }

  if (page === 'editProduct') {
    const productId = params.get('id');
    if (!productId) return '/';
    params.delete('id');
    return appendQuery(`/announcements/${encodeURIComponent(productId)}/edit`, params);
  }

  return appendQuery(staticPaths[page] ?? '/', params);
};

/**
 * Click handler for in-app links rendered as <a href>: a plain click navigates inside the app,
 * while modified clicks (new tab, new window) keep the browser's own behaviour.
 */
export const appLinkClick = (navigate?: () => void) => (event: MouseEvent<HTMLAnchorElement>) => {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return;
  }
  event.preventDefault();
  navigate?.();
};

const routeFromPage = (page: AppPage, params: URLSearchParams): AppRoute => {
  if (page === 'productDetail') {
    const productId = params.get('id');
    return productId
      ? { page, params, productId }
      : { page: 'home', params: new URLSearchParams() };
  }
  if (page === 'editProduct') {
    const productId = params.get('id');
    return productId
      ? { page, params, productId }
      : { page: 'home', params: new URLSearchParams() };
  }
  if (page === 'userDetails') {
    const userId = params.get('id');
    return userId
      ? { page, params, userId }
      : { page: 'home', params: new URLSearchParams() };
  }
  if (page === 'resetPassword' || page === 'verifyEmail') {
    const token = params.get('token');
    return token
      ? { page, params, token }
      : { page: 'home', params: new URLSearchParams() };
  }
  return { page, params };
};

export const getAppRoute = (): AppRoute => {
  const pathname = normalizePath(window.location.pathname);
  const params = new URLSearchParams(window.location.search);

  const canonicalSpaCategoryMatch = pathname.match(/^\/app\/categories\/([^/]+)\/([^/]+)$/);
  if (canonicalSpaCategoryMatch) {
    const categoryId = decodeURIComponent(canonicalSpaCategoryMatch[2]);
    const searchParams = new URLSearchParams({ categoryId });
    window.history.replaceState(window.history.state, '', `/search?${searchParams.toString()}`);
    return { page: 'search', params: searchParams };
  }

  const canonicalCategoryMatch = pathname.match(/^\/categories\/([^/]+)\/([^/]+)$/);
  if (canonicalCategoryMatch) {
    const searchParams = new URLSearchParams(params);
    searchParams.set('categoryId', decodeURIComponent(canonicalCategoryMatch[2]));
    return { page: 'search', params: searchParams };
  }

  const canonicalSpaProductMatch = pathname.match(/^\/app\/products\/([^/]+)\/([^/]+)$/);
  if (canonicalSpaProductMatch) {
    const slug = decodeURIComponent(canonicalSpaProductMatch[1]);
    const productId = decodeURIComponent(canonicalSpaProductMatch[2]);
    window.history.replaceState(
      window.history.state,
      '',
      createPageUrl('productDetail', { id: productId, slug })
    );
    return { page: 'productDetail', params: new URLSearchParams(), productId };
  }

  const canonicalProductMatch = pathname.match(/^\/products\/([^/]+)\/([^/]+)$/);
  if (canonicalProductMatch) {
    return {
      page: 'productDetail',
      params,
      productId: decodeURIComponent(canonicalProductMatch[2]),
    };
  }

  const spaProductId = matchPathParameter(pathname, /^\/app\/products\/([^/]+)$/);
  if (spaProductId) {
    window.history.replaceState(
      window.history.state,
      '',
      createPageUrl('productDetail', { id: spaProductId })
    );
    return { page: 'productDetail', params: new URLSearchParams(), productId: spaProductId };
  }

  const productId = matchPathParameter(pathname, /^\/products\/([^/]+)$/);
  if (productId) {
    return { page: 'productDetail', params, productId };
  }

  const canonicalSpaUserMatch = pathname.match(/^\/app\/users\/([^/]+)\/([^/]+)$/);
  if (canonicalSpaUserMatch) {
    const slug = decodeURIComponent(canonicalSpaUserMatch[1]);
    const userId = decodeURIComponent(canonicalSpaUserMatch[2]);
    window.history.replaceState(
      window.history.state,
      '',
      createPageUrl('userDetails', { id: userId, slug })
    );
    return { page: 'userDetails', params: new URLSearchParams(), userId };
  }

  const canonicalUserMatch = pathname.match(/^\/users\/([^/]+)\/([^/]+)$/);
  if (canonicalUserMatch) {
    return {
      page: 'userDetails',
      params,
      userId: decodeURIComponent(canonicalUserMatch[2]),
    };
  }

  const spaUserId = matchPathParameter(pathname, /^\/app\/users\/([^/]+)$/);
  if (spaUserId) {
    window.history.replaceState(
      window.history.state,
      '',
      createPageUrl('userDetails', { id: spaUserId })
    );
    return { page: 'userDetails', params: new URLSearchParams(), userId: spaUserId };
  }

  const userId = matchPathParameter(pathname, /^\/users\/([^/]+)$/);
  if (userId) {
    return { page: 'userDetails', params, userId };
  }

  const editProductId = matchPathParameter(
    pathname,
    /^\/announcements\/([^/]+)\/edit$/
  );
  if (editProductId) {
    return { page: 'editProduct', params, productId: editProductId };
  }

  const pathPage = pathPages.get(pathname);
  if (pathPage) {
    return routeFromPage(pathPage, params);
  }

  return { page: 'notFound', params };
};
